<template>
  <Grid v-if="pageData">
    <Heading :level="1" :content="`Opiniepanel - ${pageData.title}`" class="sr-only" />
    <Heading :level="2" class="sr-only" content="Uitgelichte artikelen" />
    <TeaserGroupSpotlightGrid
      v-if="state.featured"
      :articles="state.featured"
      class="is--full-width-small"
      :spotlight="true"
      priority="high"
      :padded="false"
      :show-dates="true"
      :analytics="{ linkAnalyticsTag: 'spotlight' }"
    />

    <CarouselInverted
      v-if="pageData.peilingtrends && pageData.peilingtrends.length > 0"
      :_index="1"
      title="Peilingtrends"
      icon="eenvandaag-type:chart-column-2"
      button-url="/peilingtrends"
      :introduction="pageData.peilingtrendsDescription"
      analytics="section-carousel-peilingtrends"
      aria-label-prefix="Peilingtrend:"
      :show-labels="false"
      :teasers="pageData.peilingtrends?.map((article) => article.articlesId) as ITeaser[]"
    />

    <TeaserGroupLoadMoreGrid
      v-if="state.related && state.related.length >= 1"
      :title="`Meer ${pageData.title}`"
      :initial-items="[...state.related]"
      :offset="(state.featured?.length || 0) + state.related.length"
      loading="lazy"
      :filter="
        useRelatedFilter(
          {
            field: 'themes',
            id: pageData.id,
          },
          true,
        )
      "
      :teaser-count="12"
      :cache-key="baseCacheKey"
      :heading-level="3"
    />
  </Grid>
</template>

<script setup lang="ts">
import usePageStore from '~/stores/page';
import useRelatedFilter from '~/composables/useRelatedFilter';
import useCollectionInstance from '~/composables/useCollectionInstance';
import type { ICmsThemes } from '~~/types/_cms-types';
import useTeasers from '~/composables/useTeasers';
import type { ICollectionTeaser, ITeaser } from '~~/types/local_types';
import useAdapter from '~/composables/useAdapter';
import useCheckRedirect from '~/composables/useCheckRedirect';
import CarouselInverted from '~/components/CarouselInverted.vue';

definePageMeta({
  showThemebar: true,
});

const pageStore = usePageStore();
const { slug } = useRoute().params;
const baseCacheKey = `themes-opinion-${slug}`;
const { getTeasers } = useTeasers();
const { dossierTeaserAdapter } = useAdapter();

const pageData = (await useCollectionInstance('opinionThemes')) as ICmsThemes;
if (!pageData) {
  throw createError({ statusCode: 404, statusMessage: 'Thema niet gevonden' });
}

usePageSeo({
  pageTitle: pageData.title,
  pageDescription: `Lees hier het laatste nieuws, achtergronden en analyses over Opiniepanel - ${pageData.title}. Blijf op de hoogte van de belangrijkste ontwikkelingen en verhalen binnen dit onderwerp.`,
  section: 'Opiniepanel',
});

if (import.meta.client) {
  sendAnalytics({
    prefix: 'themas',
    chapter1: 'opiniepanel',
    chapter2: 'themas',
    slug: pageData.slug,
  });
}

await useCheckRedirect(pageData.slug, 'opiniepanel/themas');

const themeFilter = useRelatedFilter({ field: 'themes', id: pageData.id }, true);

const dossiersFilter = {
  themes: { themes_id: pageData.id },
};

interface IState {
  dossiers?: ICollectionTeaser[];
  featured?: ITeaser[];
  related?: ITeaser[];
}

const state = reactive<IState>({
  dossiers: undefined,
  featured: undefined,
  related: undefined,
});

const getAllTeasers = async () => {
  const itemCount = 7 + 12;
  const items = await getTeasers(
    itemCount,
    themeFilter as never,
    `${baseCacheKey}-featured`,
    '-publish_time',
  );
  return {
    featured: items?.slice(0, 7),
    related: items?.slice(7),
  };
};

const { featured, related } = await getAllTeasers();
state.featured = featured;
state.related = related;

const data = await getTeasers(
  -1,
  dossiersFilter as never,
  `${baseCacheKey}-dossiers`,
  '-adjust_time',
  0,
  false,
  'dossiers',
);
state.dossiers = data?.map(dossierTeaserAdapter) as ICollectionTeaser[];

pageStore.setCurrentPage(undefined);
</script>

<style scoped lang="postcss">
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
</style>
